import React from 'react';
import { Link } from 'react-router-dom';
import LegalPageLayout from '../components/LegalPageLayout';
import { useLocale } from '../contexts/LocaleContext';
import { Mail, FileText, RotateCcw, Trash2 } from 'lucide-react';

const CONTACT = 'contact@adfini.ai';

export default function HelpSupport() {
  const { locale } = useLocale();

  return (
    <LegalPageLayout title="Help & support" locale={locale}>
      <p>
        We are here for product questions, billing issues, privacy requests, and account support.
        The fastest way to reach us is by email.
      </p>

      <section className="studio-support-contact">
        <div className="flex items-start gap-3">
          <Mail className="mt-0.5 h-5 w-5 shrink-0 text-[var(--studio-ink)]" aria-hidden />
          <div>
            <h2 className="text-lg font-medium text-[var(--studio-ink)]">Email us</h2>
            <p className="mt-2">
              <a
                className="text-[var(--studio-ink)] underline hover:text-[var(--studio-ink)]"
                href={`mailto:${CONTACT}`}
              >
                {CONTACT}
              </a>
            </p>
            <p className="mt-2 text-[var(--studio-ink)]">
              We review messages as promptly as we can. Complex requests (such as account data
              deletion) may take additional time while we verify identity and complete technical
              steps.
            </p>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">What we can help with</h2>
        <ul className="space-y-4">
          <li className="flex gap-3">
            <FileText className="mt-0.5 h-5 w-5 shrink-0 text-[var(--studio-ink)]" aria-hidden />
            <div>
              <strong className="text-[var(--studio-ink)]">General questions</strong>
              <p className="mt-1">
                How the product works, credits, uploads, and troubleshooting sign-in or generation
                issues.
              </p>
            </div>
          </li>
          <li className="flex gap-3">
            <RotateCcw className="mt-0.5 h-5 w-5 shrink-0 text-[var(--studio-ink)]" aria-hidden />
            <div>
              <strong className="text-[var(--studio-ink)]">Refund requests</strong>
              <p className="mt-1">
                Email <a className="text-[var(--studio-ink)] underline" href={`mailto:${CONTACT}`}>{CONTACT}</a>{' '}
                with your account email and purchase details. See our{' '}
                <Link className="text-[var(--studio-ink)] underline hover:text-[var(--studio-ink)]" to="/refund-policy">
                  Refund policy
                </Link>{' '}
                for eligibility.
              </p>
            </div>
          </li>
          <li className="flex gap-3">
            <Trash2 className="mt-0.5 h-5 w-5 shrink-0 text-[var(--studio-ink)]" aria-hidden />
            <div>
              <strong className="text-[var(--studio-ink)]">Account data deletion</strong>
              <p className="mt-1">
                To request deletion of your account and associated personal data, email{' '}
                <a className="text-[var(--studio-ink)] underline" href={`mailto:${CONTACT}`}>{CONTACT}</a>{' '}
                from your registered email (or include information we can use to verify ownership).
                We will confirm receipt and explain any items we must retain for legal or fraud-
                prevention reasons, if applicable.
              </p>
            </div>
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Privacy and terms</h2>
        <p>
          For how we handle personal data, see our{' '}
          <Link className="text-[var(--studio-ink)] underline hover:text-[var(--studio-ink)]" to="/privacy">
            Privacy policy
          </Link>
          . For rules of use, credits, and limitations, see our{' '}
          <Link className="text-[var(--studio-ink)] underline hover:text-[var(--studio-ink)]" to="/terms">
            Terms of Service
          </Link>
          .
        </p>
      </section>
    </LegalPageLayout>
  );
}
